import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Send, StopCircle, Sparkles, FileCode2, Cpu, Folder, FileText, RefreshCw, Globe, Box, AlertTriangle, ExternalLink, MessageSquare, Loader2, Paperclip, X as XIcon, Image as ImageIcon, FolderOpen, ChevronDown, Plus, Check, Trash2, ListChecks, BookOpenCheck, Wand2, Save, Play, Video, Zap } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import TemplatesModal from '../components/snagcart/TemplatesModal';
import SettingsModal from '../components/snagcart/SettingsModal';
import ProjectSwitcher from '../components/snagcart/ProjectSwitcher';
import ProjectActions from '../components/snagcart/ProjectActions';
import VoiceInput from '../components/snagcart/VoiceInput';
import { readSnagcartSettings } from '../hooks/useSnagcartSettings';
import { gptPilotPreviewUrl, gptPilotDevPreviewUrl, isUnsafePreviewPortUrl, normalizePreviewUrl } from '../utils/previewHtml';
import { formatApiDetail, parseResponseJson } from '../lib/http';
import { LayoutTemplate, Settings as SettingsIcon } from 'lucide-react';

const API_URL = (process.env.REACT_APP_BACKEND_URL && process.env.REACT_APP_BACKEND_URL !== '') ? process.env.REACT_APP_BACKEND_URL : '';

const LS = {
  ACTIVE_JOB: 'gpt_pilot_active_job',
  LAST_JOB: 'gpt_pilot_last_job',           // persisted even after completion
  FORM_NAME: 'gpt_pilot_form_name',
  FORM_DESC: 'gpt_pilot_form_description',
};

const FULL_STACK_RE = /\b(api|backend|server|database|auth|crud|fastapi|express|full[- ]?stack|e-?commerce|marketplace|dashboard|todo app|rest api|graphql)\b/i;
const LANDING_PAGE_RE = /\b(landing\s*page|lamdin\s*page|one[- ]?page|single[- ]?page|marketing\s+page|portfolio\s+site|brochure\s+site|coffee\s+shop|tea\s+shop|caf[eé]|bakery|bistro|restaurant|barista)\b/i;

function wantsFullStack(desc) {
  return FULL_STACK_RE.test((desc || '').trim());
}

function isSimpleLandingPrompt(desc) {
  const text = (desc || '').trim();
  if (!text || wantsFullStack(text)) return false;
  if (!LANDING_PAGE_RE.test(text)) return false;
  return text.length <= 280;
}

/** Respect explicit New Project toggle; otherwise frontend-only for landing prompts. */
function resolveIncludeBackend(desc) {
  try {
    const stored = sessionStorage.getItem('snagcart_chat_include_backend');
    if (stored === '0') return false;
    if (stored === '1') return true;
  } catch { /* ignore */ }
  return !isSimpleLandingPrompt(desc);
}

const TYPE_STYLES = {
  status:   { color: 'text-sky-300',    label: 'STATUS'  },
  message:  { color: 'text-zinc-200',   label: 'MSG'     },
  stream:   { color: 'text-emerald-300',label: 'STREAM'  },
  stage:    { color: 'text-violet-300', label: 'STAGE'   },
  stats:    { color: 'text-amber-300',  label: 'STATS'   },
  logs:     { color: 'text-zinc-500',   label: 'LOG'     },
  project_info: { color: 'text-cyan-300', label: 'INFO' },
  done:     { color: 'text-emerald-400',label: 'DONE'    },
  error:    { color: 'text-red-400',    label: 'ERROR'   },
};

export default function GPTPilot() {
  const navigate = useNavigate();
  const [name, setName] = useState(() => localStorage.getItem(LS.FORM_NAME) || '');
  const [description, setDescription] = useState(() => localStorage.getItem(LS.FORM_DESC) || '');
  const [jobId, setJobId] = useState(null);
  const [status, setStatus] = useState(null);
  const [events, setEvents] = useState([]);
  const [running, setRunning] = useState(false);
  // Bumping this remounts ChatPanel so it re-reads the sessionStorage prefill —
  // used when the user drafts a new project from the My Projects dropdown.
  const [chatRefresh, setChatRefresh] = useState(0);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const esRef = useRef(null);
  const logRef = useRef(null);

  // Persist form input as user types so a refresh during build setup doesn't
  // wipe the description.
  useEffect(() => { localStorage.setItem(LS.FORM_NAME, name); }, [name]);
  useEffect(() => { localStorage.setItem(LS.FORM_DESC, description); }, [description]);

  const subscribe = useCallback((id) => {
    if (esRef.current) {
      esRef.current.close();
      esRef.current = null;
    }
    const token = localStorage.getItem('token') || '';
    const url = `${API_URL}/api/gpt-pilot/projects/${id}/stream?token=${encodeURIComponent(token)}`;
    const es = new EventSource(url);
    esRef.current = es;
    es.onmessage = (ev) => {
      try {
        const payload = JSON.parse(ev.data);
        if (payload.type === 'end') {
          es.close();
          esRef.current = null;
          setRunning(false);
          // Clear active-job marker, keep last-job marker so refresh shows files.
          localStorage.removeItem(LS.ACTIVE_JOB);
          return;
        }
        setEvents((prev) => [...prev, payload]);
        if (payload.type === 'done') {
          setStatus(payload.success ? 'completed' : 'failed');
        } else if (payload.type === 'error') {
          setStatus('error');
        } else if (payload.type === 'status' || payload.type === 'stage') {
          setStatus('running');
        }
      } catch (e) {
        // ignore parse errors
      }
    };
    es.onerror = () => {
      // Don't tear down on transient errors; EventSource auto-reconnects.
    };
  }, []);

  // Heartbeat fallback: while a build is running, poll the job status every
  // 20s. SSE is the primary channel, but if the backend restarts mid-build
  // (e.g. an OOM-killed worker that comes back up) the terminal "done"/"end"
  // event can be lost, leaving the UI spinning forever. Polling guarantees we
  // still flip to the finished state and surface the generated files.
  useEffect(() => {
    if (!running || !jobId) return undefined;
    const id = setInterval(async () => {
      try {
        const r = await fetch(`${API_URL}/api/gpt-pilot/projects/${jobId}/status`, { headers: authHeaders() });
        if (!r.ok) return;
        const info = await r.json();
        const terminal = ['completed', 'failed', 'error', 'interrupted'];
        if (info && terminal.includes(info.status)) {
          if (esRef.current) { esRef.current.close(); esRef.current = null; }
          setStatus(info.status);
          setRunning(false);
          localStorage.removeItem(LS.ACTIVE_JOB);
          if (info.status === 'completed') {
            toast.success('Build complete! Your app is ready.');
          } else if (info.status === 'interrupted') {
            toast('Build finished but the live connection dropped — showing the result.');
          }
        }
      } catch (_) {
        // network blip — keep polling
      }
    }, 20000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running, jobId]);

  const authHeaders = () => {
    const t = localStorage.getItem('token');
    return t ? { Authorization: `Bearer ${t}` } : {};
  };

  // Auto-restore the user's session on mount:
  //  • URL `?job=<id>` wins (used by Dashboard "Edit" / "AI Agent" buttons).
  //  • `?resume=1` also asks the chat composer to pre-fill a continuation prompt.
  //  • Else if there's an in-flight job (ACTIVE_JOB), reconnect SSE.
  //  • Else if there's a previous completed job (LAST_JOB), restore the
  //    job_id + status so the Files/Preview tabs survive a refresh.
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const fromUrl = urlParams.get('job');
    const wantResume = urlParams.get('resume') === '1';
    const active = localStorage.getItem(LS.ACTIVE_JOB);
    const last = localStorage.getItem(LS.LAST_JOB);
    const target = fromUrl || active || last;
    if (wantResume) {
      // ChatPanel reads this on mount to pre-fill its composer.
      try { sessionStorage.setItem('snagcart_chat_prefill', 'Continue building from where the previous build was interrupted. Review the existing files and pick the next logical task.'); } catch { /* ignore */ }
    }
    if (!target) return;

    fetch(`${API_URL}/api/gpt-pilot/projects/${target}/status`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : null))
      .then((info) => {
        if (!info || !info.status) {
          localStorage.removeItem(LS.ACTIVE_JOB);
          localStorage.removeItem(LS.LAST_JOB);
          return;
        }
        setJobId(target);
        setStatus(info.status);
        // Persist this target so refresh continues to work.
        localStorage.setItem(LS.LAST_JOB, target);
        const isLive = info.status !== 'completed' && info.status !== 'error' && info.status !== 'failed' && info.status !== 'interrupted';
        if (isLive) {
          setRunning(true);
          setEvents([]);
          localStorage.setItem(LS.ACTIVE_JOB, target);
          subscribe(target);
        } else {
          setRunning(false);
          localStorage.removeItem(LS.ACTIVE_JOB);
        }
      })
      .catch(() => {
        localStorage.removeItem(LS.ACTIVE_JOB);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => () => { if (esRef.current) esRef.current.close(); }, []);

  useEffect(() => {
    if (logRef.current) {
      logRef.current.scrollTop = logRef.current.scrollHeight;
    }
  }, [events]);

  const stopStream = () => {
    if (esRef.current) {
      esRef.current.close();
      esRef.current = null;
    }
    setRunning(false);
    localStorage.removeItem(LS.ACTIVE_JOB);
  };

  const startProject = async (overrideDescription) => {
    const desc = (overrideDescription ?? description).trim();
    if (!desc) {
      toast.error('Type what you want to build');
      return null;
    }
    // If the user came in via the My Projects "draft" flow, they may have
    // already picked a name and a backend preference. Reuse those instead
    // of generating a random suffix.
    const draftName = (name || '').trim();
    const projectName = draftName
      ? draftName
      : (() => {
          const baseName = (desc.split(/\s+/).slice(0, 4).join('-').toLowerCase().replace(/[^a-z0-9-]/g, ''))
            .replace(/-+/g, '-').replace(/^-|-$/g, '') || 'snagcart-app';
          return `${baseName}-${Math.random().toString(36).slice(2, 6)}`;
        })();
    const includeBackend = resolveIncludeBackend(desc);
    if (isSimpleLandingPrompt(desc) && !includeBackend) {
      toast.message('Landing page detected — building frontend-only for a faster result');
    }
    setName(projectName);
    setDescription(desc);
    setEvents([]);
    setStatus('queued');
    setRunning(true);

    try {
      const res = await fetch(`${API_URL}/api/gpt-pilot/projects`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ name: projectName, description: desc, include_backend: includeBackend }),
      });
      if (res.status === 401) {
        toast.error('Please sign in to start a build');
        navigate('/auth');
        setRunning(false);
        return null;
      }
      if (res.status === 429) {
        const err = await res.json().catch(() => ({ detail: 'Rate limit reached' }));
        toast.error(err.detail || 'Rate limit reached');
        setRunning(false);
        setStatus('blocked');
        return null;
      }
      if (res.status === 409) {
        const err = await res.json().catch(() => ({ detail: 'A project with that name already exists' }));
        toast.error(err.detail || 'A project with that name already exists');
        setRunning(false);
        setStatus(null);
        return null;
      }
      if (!res.ok) {
        const err = await res.text();
        throw new Error(err || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setJobId(data.job_id);
      localStorage.setItem(LS.ACTIVE_JOB, data.job_id);
      localStorage.setItem(LS.LAST_JOB, data.job_id);
      try { sessionStorage.removeItem('snagcart_chat_include_backend'); } catch { /* ignore */ }
      subscribe(data.job_id);
      return data.job_id;
    } catch (e) {
      toast.error(`Failed to start project: ${e.message}`);
      setRunning(false);
      setStatus('error');
      return null;
    }
  };

  return (
    <div className="min-h-screen bg-[#080808] text-white" data-testid="gpt-pilot-page">
      <header className="sticky top-0 z-30 backdrop-blur bg-[#080808]/80 border-b border-white/[0.06]">
        <div className="max-w-[1700px] mx-auto px-4 md:px-6 py-3 flex items-center gap-3">
          <button
            onClick={() => navigate('/dashboard')}
            data-testid="gpt-pilot-back-btn"
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg hover:bg-white/[0.06] text-zinc-400 hover:text-white text-sm transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Back to Dashboard</span>
          </button>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-600 to-teal-600 flex items-center justify-center">
              <Cpu className="w-4 h-4" />
            </div>
            <div>
              <h1 className="text-base font-semibold">Snagcart Builder</h1>
              <p className="text-xs text-zinc-500">Plan, write & build full-stack apps step by step</p>
            </div>
          </div>
          <div className="flex-1" />
          <ProjectActions jobId={jobId} disabled={running} />
          <button
            type="button"
            onClick={() => setSettingsOpen(true)}
            data-testid="open-settings-btn"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium text-zinc-300 hover:text-white border border-white/[0.08] hover:border-white/[0.16] hover:bg-white/[0.04] transition-colors"
            title="Snagcart settings"
          >
            <SettingsIcon className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Settings</span>
          </button>
          <ProjectSwitcher
            jobId={jobId}
            onSelect={(j) => {
              setEvents([]);
              setStatus(j.status);
              setName(j.name || '');
              setJobId(j.job_id);
              localStorage.setItem(LS.LAST_JOB, j.job_id);
              const isLive = j.status && !['completed','error','failed','interrupted'].includes(j.status);
              if (isLive) {
                setRunning(true);
                localStorage.setItem(LS.ACTIVE_JOB, j.job_id);
                subscribe(j.job_id);
              } else {
                setRunning(false);
                localStorage.removeItem(LS.ACTIVE_JOB);
              }
            }}
            onCreated={(j) => {
              // A new project was just queued from the inline form — jump
              // straight into its live build stream.
              setEvents([]);
              setStatus('queued');
              setName(j.name || '');
              setDescription(j.description || '');
              setJobId(j.job_id);
              setRunning(true);
              localStorage.setItem(LS.LAST_JOB, j.job_id);
              localStorage.setItem(LS.ACTIVE_JOB, j.job_id);
              subscribe(j.job_id);
            }}
            onDraft={(d) => {
              // Drafted, NOT started. Clear any running job, populate the
              // composer with the user's description, and let them hit Send
              // when they're happy with the prompt.
              if (esRef.current) { esRef.current.close(); esRef.current = null; }
              setJobId(null);
              setStatus(null);
              setEvents([]);
              setRunning(false);
              setName(d.name);
              const prefill = (d.description || '').trim() || `Build a ${d.include_backend ? 'full-stack' : 'frontend-only'} web app called "${d.name}".`;
              setDescription(prefill);
              localStorage.removeItem(LS.ACTIVE_JOB);
              localStorage.removeItem(LS.LAST_JOB);
              localStorage.setItem(LS.FORM_NAME, d.name);
              localStorage.setItem(LS.FORM_DESC, prefill);
              // Seed the chat composer + remount it so the prefill lands.
              try { sessionStorage.setItem('snagcart_chat_prefill', prefill); } catch { /* ignore */ }
              setChatRefresh((n) => n + 1);
              // Remember the user's include_backend preference for when send fires.
              try { sessionStorage.setItem('snagcart_chat_include_backend', d.include_backend ? '1' : '0'); } catch { /* ignore */ }
            }}
            onNew={() => {
              // Stop any active stream then clear all state for a fresh build.
              if (esRef.current) { esRef.current.close(); esRef.current = null; }
              setJobId(null);
              setStatus(null);
              setEvents([]);
              setRunning(false);
              setName('');
              setDescription('');
              localStorage.removeItem(LS.ACTIVE_JOB);
              localStorage.removeItem(LS.LAST_JOB);
              localStorage.removeItem(LS.FORM_NAME);
              localStorage.removeItem(LS.FORM_DESC);
            }}
          />
          {status && (
            <span
              data-testid="gpt-pilot-status"
              className={`px-2.5 py-1 rounded-md text-xs font-medium border ${
                status === 'running' ? 'border-sky-500/30 text-sky-300 bg-sky-500/10' :
                status === 'completed' ? 'border-emerald-500/30 text-emerald-300 bg-emerald-500/10' :
                status === 'error' || status === 'failed' ? 'border-red-500/30 text-red-300 bg-red-500/10' :
                'border-zinc-500/30 text-zinc-300 bg-zinc-500/10'
              }`}
            >{status}</span>
          )}
        </div>
      </header>

      <main className="max-w-[1700px] mx-auto px-4 md:px-6 py-4 grid grid-cols-1 md:grid-cols-12 gap-4">
        {/* Unified chat composer — Emergent-style: one input drives both
            "create new project" and "iterate on existing project". */}
        <section className="md:col-span-4">
          <div className="bg-[#0d0d0d] border border-white/[0.06] rounded-2xl overflow-hidden">
            <div className="px-4 py-2 border-b border-white/[0.06] flex items-center justify-between">
              <span className="text-xs font-medium text-zinc-300 inline-flex items-center gap-1.5">
                <MessageSquare className="w-3.5 h-3.5 text-violet-300" />
                {jobId ? 'Continue building' : 'Snagcart Builder'}
              </span>
              <button
                type="button"
                onClick={() => setTemplatesOpen(true)}
                disabled={running}
                data-testid="open-templates-btn"
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-medium border border-violet-500/30 bg-violet-500/10 text-violet-200 hover:bg-violet-500/20 hover:border-violet-500/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <LayoutTemplate className="w-3 h-3" />
                Templates
              </button>
            </div>
            <ChatPanel
              key={`chat-${chatRefresh}`}
              jobId={jobId}
              authHeader={authHeaders}
              onStartProject={startProject}
              running={running}
              status={status}
              onStop={stopStream}
            />
          </div>
        </section>

        {/* Right column — tab switcher between Stream, Files and Preview */}
        <RightPanel jobId={jobId} events={events} logRef={logRef} running={running} />
      </main>

      <TemplatesModal
        open={templatesOpen}
        onClose={() => setTemplatesOpen(false)}
        onPick={(tpl) => {
          // Make project name unique-ish so multiple tries don't collide.
          const suffix = Math.random().toString(36).slice(2, 6);
          const projectName = `${(tpl.project_name || tpl.name).toLowerCase().replace(/[^a-z0-9]+/g, '-')}-${suffix}`;
          setName(projectName);
          setDescription(tpl.description);
          localStorage.setItem(LS.FORM_NAME, projectName);
          localStorage.setItem(LS.FORM_DESC, tpl.description);
          setTemplatesOpen(false);
          toast.success(`Loaded "${tpl.name}" template — edit and hit Start Build`);
        }}
      />

      <SettingsModal
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        onWiped={() => {
          // Reset client-side state after a wipe so stale jobIds don't linger.
          if (esRef.current) { esRef.current.close(); esRef.current = null; }
          setJobId(null);
          setStatus(null);
          setEvents([]);
          setRunning(false);
          localStorage.removeItem(LS.ACTIVE_JOB);
          localStorage.removeItem(LS.LAST_JOB);
        }}
      />
    </div>
  );
}

function RightPanel({ jobId, events, logRef, running }) {
  const [previewRefresh, setPreviewRefresh] = useState(0);
  const [tab, setTab] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const t = params.get('tab');
      const hasJob = params.get('job');
      // Emergent-style: ?tab=chat&job=… keeps chat on the left, preview on the right.
      if (t === 'chat' && hasJob) return 'preview';
      if (!t && hasJob) return 'preview';
      return ['stream', 'files', 'preview', 'plan', 'rules'].includes(t) ? t : 'stream';
    } catch {
      return 'stream';
    }
  });

  // When the agent build finishes, refresh the live preview automatically.
  const prevRunning = useRef(running);
  useEffect(() => {
    if (prevRunning.current && !running && jobId) {
      setPreviewRefresh((n) => n + 1);
      setTab((cur) => (cur === 'stream' ? 'preview' : cur));
    }
    prevRunning.current = running;
  }, [running, jobId]);
  const [files, setFiles] = useState([]);
  const [projectDir, setProjectDir] = useState(null);
  const [filesLoading, setFilesLoading] = useState(false);
  const [selectedPath, setSelectedPath] = useState(null);
  const [content, setContent] = useState(null);
  const [contentLoading, setContentLoading] = useState(false);

  // Detect the most recent app_link event (live dev-server proxy URL).
  // Used as a *fallback* preview URL when no static dist/ build is available.
  const previewUrl = useMemo(() => {
    for (let i = events.length - 1; i >= 0; i--) {
      const e = events[i];
      if (e?.type === 'app_link') {
        const url = typeof e.content === 'string' ? e.content : '';
        if (!url) continue;
        const normalized = normalizePreviewUrl(url) || url;
        // Dev servers run on the backend — /preview-port/* only proxies localhost on
        // this machine and loops when the port matches the CRA dev server (:3001).
        if (normalized.includes('/preview-port/') || isUnsafePreviewPortUrl(normalized)) continue;
        if (normalized.startsWith('/api/gpt-pilot/preview/')) {
          return `${API_URL}${normalized}`;
        }
        if (normalized.startsWith('http')) return normalized;
        return `${window.location.origin}${normalized.startsWith('/') ? '' : '/'}${normalized}`;
      }
    }
    return null;
  }, [events]);

  const authHeader = useCallback(() => {
    const t = localStorage.getItem('token');
    return t ? { Authorization: `Bearer ${t}` } : {};
  }, []);

  const fetchFiles = useCallback(async () => {
    if (!jobId) return;
    setFilesLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/gpt-pilot/projects/${jobId}/files`, { headers: authHeader() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setFiles(data.files || []);
      setProjectDir(data.project_dir);
    } catch (e) {
      toast.error('Could not load files');
    } finally {
      setFilesLoading(false);
    }
  }, [jobId, authHeader]);

  const loadFileContent = useCallback(async (path) => {
    if (!jobId || !path) return;
    setSelectedPath(path);
    setContent(null);
    setContentLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/gpt-pilot/projects/${jobId}/files/content?path=${encodeURIComponent(path)}`, { headers: authHeader() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setContent(data);
    } catch (e) {
      toast.error('Could not read file');
    } finally {
      setContentLoading(false);
    }
  }, [jobId, authHeader]);

  useEffect(() => {
    if (tab === 'files' && jobId) fetchFiles();
  }, [tab, jobId, fetchFiles]);

  return (
    <section className="md:col-span-8">
      <div className="bg-[#0d0d0d] border border-white/[0.06] rounded-2xl overflow-hidden">
        <div className="px-5 py-2.5 border-b border-white/[0.06] flex items-center gap-1">
          <button
            data-testid="tab-stream"
            onClick={() => setTab('stream')}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${tab==='stream' ? 'bg-white/[0.08] text-white' : 'text-zinc-400 hover:text-zinc-200'}`}
          >
            <FileCode2 className="w-3.5 h-3.5 inline mr-1.5" />
            Build stream
          </button>
          <button
            data-testid="tab-files"
            onClick={() => setTab('files')}
            disabled={!jobId}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors disabled:opacity-40 ${tab==='files' ? 'bg-white/[0.08] text-white' : 'text-zinc-400 hover:text-zinc-200'}`}
          >
            <Folder className="w-3.5 h-3.5 inline mr-1.5" />
            Files {files.length > 0 && <span className="text-zinc-500 ml-1">({files.length})</span>}
          </button>
          <button
            data-testid="tab-preview"
            onClick={() => setTab('preview')}
            disabled={!jobId}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors disabled:opacity-40 ${tab==='preview' ? 'bg-white/[0.08] text-white' : 'text-zinc-400 hover:text-zinc-200'}`}
            title={jobId ? 'In-browser preview of the generated frontend' : 'Start a build to enable preview'}
          >
            <Box className="w-3.5 h-3.5 inline mr-1.5" />
            Preview
          </button>
          <button
            data-testid="tab-plan"
            onClick={() => setTab('plan')}
            disabled={!jobId}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors disabled:opacity-40 ${tab==='plan' ? 'bg-white/[0.08] text-white' : 'text-zinc-400 hover:text-zinc-200'}`}
            title={jobId ? 'Plan Mode — questionnaire-driven implementation plan (Dyad-style)' : 'Start a build first'}
          >
            <ListChecks className="w-3.5 h-3.5 inline mr-1.5" />
            Plan
          </button>
          <button
            data-testid="tab-rules"
            onClick={() => setTab('rules')}
            disabled={!jobId}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors disabled:opacity-40 ${tab==='rules' ? 'bg-white/[0.08] text-white' : 'text-zinc-400 hover:text-zinc-200'}`}
            title={jobId ? 'Project rules.md — durable agent instructions' : 'Start a build first'}
          >
            <BookOpenCheck className="w-3.5 h-3.5 inline mr-1.5" />
            Rules
          </button>
          <div className="flex-1" />
          {jobId && (
            <button
              data-testid="render-video-btn"
              onClick={async () => {
                try {
                  toast.info('Rendering video — this takes ~10–20 s…');
                  const res = await fetch(`${API_URL}/api/gpt-pilot/projects/${jobId}/render-video?duration_s=6`, {
                    method: 'POST',
                    headers: authHeader(),
                  });
                  if (!res.ok) {
                    const err = await res.text();
                    throw new Error(err || `HTTP ${res.status}`);
                  }
                  const blob = await res.blob();
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `snagcart-${jobId.slice(0, 8)}.mp4`;
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  setTimeout(() => URL.revokeObjectURL(url), 5000);
                  toast.success('Video downloaded');
                } catch (e) {
                  toast.error(`Video render failed: ${e.message}`);
                }
              }}
              className="px-2.5 py-1.5 rounded-md text-[11px] font-medium text-zinc-300 hover:text-white hover:bg-white/[0.06] transition-colors"
              title="Render the live preview as an MP4 (scrolling capture). Inspired by blink-new/hyperframes."
            >
              <Video className="w-3.5 h-3.5 inline mr-1" />
              Render video
            </button>
          )}
          {tab === 'files' && (
            <button
              onClick={fetchFiles}
              data-testid="files-refresh"
              className="p-1.5 rounded-md hover:bg-white/[0.06] text-zinc-400"
              title="Refresh file list"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${filesLoading ? 'animate-spin' : ''}`} />
            </button>
          )}
          {jobId && (
            <span className="text-[10px] text-zinc-500 font-mono mr-1">job {jobId.slice(0, 8)}</span>
          )}
        </div>

        {tab === 'stream' && (
          <div
            ref={logRef}
            data-testid="gpt-pilot-stream"
            className="h-[calc(100vh-160px)] min-h-[520px] overflow-y-auto px-5 py-4 font-mono text-xs leading-relaxed space-y-1.5"
          >
            {events.length === 0 && !running && (
              <p className="text-zinc-600 italic">Stream will appear here once a build starts…</p>
            )}
            {events.length === 0 && running && (
              <p className="text-zinc-500 animate-pulse">Initializing Snagcart Builder…</p>
            )}
            {events.filter((ev) => {
              // Event Logging setting controls whether 'logs' rows are shown.
              const cfg = readSnagcartSettings();
              return cfg?.core?.event_logging === false ? ev.type !== 'logs' : true;
            }).map((ev, i) => {
              const s = TYPE_STYLES[ev.type] || { color: 'text-zinc-400', label: ev.type?.toUpperCase() || '·' };
              return (
                <div key={i} className="flex gap-3">
                  <span className={`flex-shrink-0 ${s.color} font-semibold w-16`}>{s.label}</span>
                  <pre className={`whitespace-pre-wrap break-words flex-1 ${s.color}`}>
                    {typeof ev.content === 'string' ? ev.content : JSON.stringify(ev.content)}
                  </pre>
                </div>
              );
            })}
          </div>
        )}

        {tab === 'files' && (
          <div className="h-[calc(100vh-160px)] min-h-[520px] grid grid-cols-12" data-testid="files-pane">
            <div className="col-span-4 border-r border-white/[0.06] overflow-y-auto px-2 py-2">
              <div className="flex items-center justify-between px-2 pb-2 mb-1 border-b border-white/[0.04]">
                <span className="text-[10px] text-zinc-500 uppercase tracking-wider font-semibold">Files</span>
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      const tid = (window.toast || (() => {}))?.loading?.('Rebuilding…');
                      const res = await fetch(`${API_URL}/api/gpt-pilot/projects/${jobId}/build`, {
                        method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeader() },
                      });
                      const rawRebuild = await res.text();
                      let data = {};
                      try { data = JSON.parse(rawRebuild); } catch { data = { detail: rawRebuild }; }
                      if (res.ok && data.status === 'success') {
                        toast.success('Rebuild complete — preview refreshed');
                      } else {
                        toast.error(data.detail || data.error || 'Rebuild failed');
                      }
                    } catch (e) { toast.error(`Rebuild failed: ${e.message}`); }
                  }}
                  disabled={!jobId}
                  data-testid="files-rebuild-btn"
                  title="Rebuild the project bundle (skips npm install if node_modules exists — usually 3-5s)"
                  className="inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-medium border border-cyan-500/30 bg-cyan-500/10 text-cyan-200 hover:bg-cyan-500/20 hover:border-cyan-500/50 disabled:opacity-50"
                >
                  <RefreshCw className="w-2.5 h-2.5" />
                  Rebuild
                </button>
              </div>
              {projectDir && (
                <div className="text-[10px] text-zinc-500 px-2 py-1 font-mono truncate">{projectDir}</div>
              )}
              {files.length === 0 && !filesLoading && (
                <p className="text-zinc-600 text-xs px-2 py-3 italic">
                  No files yet. The workspace appears once Snagcart Builder scaffolds the project.
                </p>
              )}
              {files.map((f) => {
                const active = f.path === selectedPath;
                return (
                  <button
                    key={f.path}
                    onClick={() => loadFileContent(f.path)}
                    data-testid={`file-item-${f.path.replace(/[^a-zA-Z0-9]/g,'_')}`}
                    className={`w-full text-left px-2 py-1 rounded text-xs font-mono truncate flex items-center gap-1.5 ${active ? 'bg-violet-500/15 text-violet-200' : 'text-zinc-400 hover:bg-white/[0.04]'}`}
                  >
                    <FileText className="w-3 h-3 flex-shrink-0 opacity-70" />
                    <span className="truncate">{f.path}</span>
                  </button>
                );
              })}
            </div>
            <div className="col-span-8 overflow-y-auto">
              {!selectedPath && (
                <p className="text-zinc-600 text-xs px-5 py-5 italic">Select a file on the left to preview its contents.</p>
              )}
              {selectedPath && contentLoading && (
                <p className="text-zinc-500 text-xs px-5 py-5 animate-pulse">Loading…</p>
              )}
              {selectedPath && !contentLoading && content && (
                <>
                  <div className="px-5 py-2 border-b border-white/[0.06] flex items-center justify-between">
                    <span className="text-xs font-mono text-zinc-300 truncate">{selectedPath}</span>
                    <span className="text-[10px] text-zinc-500">{content.size} bytes</span>
                  </div>
                  <pre className="px-5 py-3 text-xs leading-relaxed font-mono whitespace-pre overflow-x-auto text-zinc-200">
                    {content.content}
                  </pre>
                </>
              )}
            </div>
          </div>
        )}

        {tab === 'preview' && (
          <PreviewPane
            jobId={jobId}
            fallbackUrl={previewUrl}
            authHeader={authHeader}
            refreshToken={previewRefresh}
            agentRunning={running}
          />
        )}
        {tab === 'plan' && (
          <PlanPanel jobId={jobId} authHeader={authHeader} />
        )}
        {tab === 'rules' && (
          <RulesPanel jobId={jobId} authHeader={authHeader} />
        )}
      </div>
    </section>
  );
}


// --- Plan Mode panel (Dyad-inspired) --------------------------------------
// Three-step flow:
//   1) User types a goal → POST /plan/draft → 3-5 clarifying questions.
//   2) User answers → POST /plan/finalize → markdown plan persisted on job.
//   3) "Implement" → POST /plan/implement → triggers a regular chat iteration
//      with the plan as the user message. Heavily reduces ambiguity-driven
//      misses ("I asked for X, agent did Y").
// --------------------------------------------------------------------------
function PlanPanel({ jobId, authHeader }) {
  const [goal, setGoal] = useState('');
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [plan, setPlan] = useState(null); // {goal, plan_md, answers, implemented, ...}
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState('idle'); // 'idle' | 'draft' | 'questions' | 'final' | 'done'

  // Fetch any persisted plan on mount.
  useEffect(() => {
    if (!jobId) return;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/gpt-pilot/projects/${jobId}/plan`, { headers: authHeader() });
        if (!r.ok) return;
        const data = await r.json();
        if (data?.plan?.plan_md) {
          setPlan(data.plan);
          setGoal(data.plan.goal || '');
          setStep('done');
        }
      } catch { /* noop */ }
    })();
  }, [jobId, authHeader]);

  const draft = async () => {
    if (!goal.trim()) { toast.error('Describe what you want first'); return; }
    setBusy(true);
    try {
      const r = await fetch(`${API_URL}/api/gpt-pilot/projects/${jobId}/plan/draft`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({ goal }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.detail || `HTTP ${r.status}`);
      setQuestions(data.questions || []);
      // Seed empty answers.
      const seed = {};
      (data.questions || []).forEach((q) => {
        seed[q.id] = q.type === 'checkbox' ? [] : '';
      });
      setAnswers(seed);
      setStep('questions');
    } catch (e) {
      toast.error(`Plan draft failed: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const finalize = async () => {
    setBusy(true);
    try {
      // Serialise checkbox arrays into comma strings for prompt readability.
      const flat = {};
      Object.entries(answers).forEach(([k, v]) => {
        flat[k] = Array.isArray(v) ? v.join(', ') : (v || '');
      });
      const r = await fetch(`${API_URL}/api/gpt-pilot/projects/${jobId}/plan/finalize`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({ goal, answers: flat }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.detail || `HTTP ${r.status}`);
      setPlan(data);
      setStep('done');
      toast.success('Plan ready — review and click Implement');
    } catch (e) {
      toast.error(`Plan finalize failed: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const implement = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API_URL}/api/gpt-pilot/projects/${jobId}/plan/implement`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({ auto_build: true, auto_verify: true }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.detail || `HTTP ${r.status}`);
      const filesChanged = data?.chat_result?.files_changed?.length || 0;
      toast.success(`Plan implemented — ${filesChanged} file(s) updated. Open the Chat tab to see details.`);
      setPlan(data.plan);
    } catch (e) {
      toast.error(`Plan implement failed: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => {
    setBusy(true);
    try {
      await fetch(`${API_URL}/api/gpt-pilot/projects/${jobId}/plan`, {
        method: 'DELETE',
        headers: authHeader(),
      });
      setGoal('');
      setQuestions([]);
      setAnswers({});
      setPlan(null);
      setStep('idle');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="plan-panel" className="h-[calc(100vh-160px)] min-h-[520px] overflow-y-auto px-5 py-4 space-y-4 text-sm">
      <div>
        <h3 className="text-zinc-200 font-medium flex items-center gap-1.5"><ListChecks className="w-4 h-4 text-violet-400" /> Plan Mode</h3>
        <p className="text-[11px] text-zinc-500 mt-0.5">Dyad-inspired. The agent will ask 3–5 clarifying questions, then draft a locked-in implementation plan before writing any code.</p>
      </div>

      {step !== 'done' && (
        <div className="space-y-2">
          <label className="text-[11px] uppercase tracking-wider text-zinc-500">Your goal</label>
          <textarea
            data-testid="plan-goal-input"
            value={goal}
            onChange={(e) => setGoal(e.target.value)}
            placeholder="e.g. Rebuild the landing page for a coffee subscription startup; modern, dark, premium"
            rows={3}
            className="w-full bg-black/40 border border-white/[0.08] rounded-md px-3 py-2 text-zinc-100 placeholder-zinc-600 focus:outline-none focus:border-violet-500/40"
          />
          {step === 'idle' && (
            <button
              data-testid="plan-draft-btn"
              onClick={draft}
              disabled={busy || !goal.trim()}
              className="px-3 py-1.5 rounded-md text-xs font-medium bg-violet-600 hover:bg-violet-500 text-white disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
              Draft questions
            </button>
          )}
        </div>
      )}

      {step === 'questions' && (
        <div className="space-y-3 pt-2 border-t border-white/[0.06]">
          <p className="text-[11px] uppercase tracking-wider text-zinc-500">Clarifying questions</p>
          {questions.map((q) => (
            <div key={q.id} data-testid={`plan-q-${q.id}`} className="space-y-1.5">
              <p className="text-zinc-200 text-[13px]">{q.label}</p>
              {q.type === 'text' && (
                <input
                  type="text"
                  value={answers[q.id] || ''}
                  onChange={(e) => setAnswers((a) => ({ ...a, [q.id]: e.target.value }))}
                  className="w-full bg-black/40 border border-white/[0.08] rounded-md px-2.5 py-1.5 text-zinc-100 text-sm focus:outline-none focus:border-violet-500/40"
                />
              )}
              {q.type === 'radio' && (
                <div className="flex flex-wrap gap-1.5">
                  {q.options.map((opt) => (
                    <button
                      key={opt}
                      data-testid={`plan-q-${q.id}-opt-${opt}`}
                      onClick={() => setAnswers((a) => ({ ...a, [q.id]: opt }))}
                      className={`px-2.5 py-1 rounded-md text-xs border transition-colors ${
                        answers[q.id] === opt
                          ? 'bg-violet-500/20 border-violet-500/50 text-violet-200'
                          : 'border-white/[0.08] text-zinc-300 hover:bg-white/[0.05]'
                      }`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              )}
              {q.type === 'checkbox' && (
                <div className="flex flex-wrap gap-1.5">
                  {q.options.map((opt) => {
                    const cur = Array.isArray(answers[q.id]) ? answers[q.id] : [];
                    const checked = cur.includes(opt);
                    return (
                      <button
                        key={opt}
                        data-testid={`plan-q-${q.id}-opt-${opt}`}
                        onClick={() =>
                          setAnswers((a) => {
                            const arr = Array.isArray(a[q.id]) ? a[q.id] : [];
                            return {
                              ...a,
                              [q.id]: checked ? arr.filter((x) => x !== opt) : [...arr, opt],
                            };
                          })
                        }
                        className={`px-2.5 py-1 rounded-md text-xs border transition-colors ${
                          checked
                            ? 'bg-violet-500/20 border-violet-500/50 text-violet-200'
                            : 'border-white/[0.08] text-zinc-300 hover:bg-white/[0.05]'
                        }`}
                      >
                        {checked && <Check className="w-3 h-3 inline mr-1" />}
                        {opt}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
          <div className="flex gap-2 pt-1">
            <button
              data-testid="plan-finalize-btn"
              onClick={finalize}
              disabled={busy}
              className="px-3 py-1.5 rounded-md text-xs font-medium bg-violet-600 hover:bg-violet-500 text-white disabled:opacity-40 inline-flex items-center gap-1.5"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
              Draft the plan
            </button>
            <button
              onClick={() => { setStep('idle'); setQuestions([]); setAnswers({}); }}
              disabled={busy}
              className="px-3 py-1.5 rounded-md text-xs font-medium text-zinc-400 hover:bg-white/[0.05]"
            >
              Back
            </button>
          </div>
        </div>
      )}

      {step === 'done' && plan?.plan_md && (
        <div className="space-y-3 pt-2 border-t border-white/[0.06]">
          <div className="flex items-center justify-between">
            <p className="text-[11px] uppercase tracking-wider text-emerald-300/80 flex items-center gap-1.5">
              <Check className="w-3 h-3" /> Plan ready{plan.implemented && ' · implemented'}
            </p>
            <button
              data-testid="plan-reset-btn"
              onClick={reset}
              disabled={busy}
              className="text-[10px] text-zinc-500 hover:text-zinc-300"
            >
              Clear & start over
            </button>
          </div>
          <pre
            data-testid="plan-markdown"
            className="bg-black/40 border border-white/[0.06] rounded-md p-3 text-[12px] text-zinc-300 whitespace-pre-wrap leading-relaxed max-h-[55vh] overflow-y-auto"
          >{plan.plan_md}</pre>
          <button
            data-testid="plan-implement-btn"
            onClick={implement}
            disabled={busy}
            className="w-full px-3 py-2 rounded-md text-sm font-medium bg-emerald-600 hover:bg-emerald-500 text-white disabled:opacity-40 inline-flex items-center justify-center gap-1.5"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
            {plan.implemented ? 'Re-implement plan' : 'Implement this plan'}
          </button>
        </div>
      )}
    </div>
  );
}


// --- Rules.md panel (Dyad-inspired) --------------------------------------
// Persistent agent instructions stored at <fe_root>/.snagcart/rules.md.
// Auto-read on every chat turn → fixes "instruction amnesia".
// --------------------------------------------------------------------------
function RulesPanel({ jobId, authHeader }) {
  const [content, setContent] = useState('');
  const [defaultTemplate, setDefaultTemplate] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!jobId) return;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/gpt-pilot/projects/${jobId}/rules`, { headers: authHeader() });
        if (!r.ok) return;
        const data = await r.json();
        setContent(data.content || '');
        setDefaultTemplate(data.default || '');
        setLoaded(true);
      } catch { /* noop */ }
    })();
  }, [jobId, authHeader]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${API_URL}/api/gpt-pilot/projects/${jobId}/rules`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({ content }),
      });
      if (!r.ok) {
        const txt = await r.text();
        throw new Error(txt || `HTTP ${r.status}`);
      }
      setDirty(false);
      toast.success('Rules saved — agent will read them on the next chat turn');
    } catch (e) {
      toast.error(`Save failed: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="rules-panel" className="h-[calc(100vh-160px)] min-h-[520px] overflow-y-auto px-5 py-4 space-y-3 text-sm">
      <div>
        <h3 className="text-zinc-200 font-medium flex items-center gap-1.5"><BookOpenCheck className="w-4 h-4 text-cyan-400" /> Project Rules</h3>
        <p className="text-[11px] text-zinc-500 mt-0.5">
          Saved at <code className="text-cyan-400/80">.snagcart/rules.md</code>. The agent reads this on every chat turn — use it to lock in brand voice, palette, files to never touch, etc.
        </p>
      </div>
      <textarea
        data-testid="rules-textarea"
        value={content}
        onChange={(e) => { setContent(e.target.value); setDirty(true); }}
        placeholder={loaded ? '(no rules yet — paste in some house rules and save)' : 'Loading…'}
        rows={22}
        className="w-full bg-black/40 border border-white/[0.08] rounded-md px-3 py-2 text-zinc-100 placeholder-zinc-600 font-mono text-[12px] leading-relaxed focus:outline-none focus:border-cyan-500/40 resize-vertical"
      />
      <div className="flex items-center gap-2">
        <button
          data-testid="rules-save-btn"
          onClick={save}
          disabled={saving || !dirty}
          className="px-3 py-1.5 rounded-md text-xs font-medium bg-cyan-600 hover:bg-cyan-500 text-white disabled:opacity-40 inline-flex items-center gap-1.5"
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          Save rules
        </button>
        {!content.trim() && defaultTemplate && (
          <button
            data-testid="rules-template-btn"
            onClick={() => { setContent(defaultTemplate); setDirty(true); }}
            className="px-3 py-1.5 rounded-md text-xs font-medium text-zinc-300 hover:bg-white/[0.05]"
          >
            Insert starter template
          </button>
        )}
        {dirty && <span className="text-[10px] text-amber-300">Unsaved changes</span>}
      </div>
    </div>
  );
}

// --- Static build + serve panel (Pythagora-style) -------------------------
// Runs `npm install && vite build` once on the backend then iframes the
// resulting dist/ from /api/gpt-pilot/preview/<job_id>/. Same URL is the
// deploy/share URL (no auth required, anyone with the link sees the site).
// --------------------------------------------------------------------------
function LiveBuildPanel({ jobId, authHeader }) {
  const [state, setState] = useState(null);  // {status, logs, preview_url, ...}
  const [busy, setBusy] = useState(false);
  const [publishUrl, setPublishUrl] = useState(null);
  const [iframeBust, setIframeBust] = useState(0);

  const fetchStatus = useCallback(async () => {
    if (!jobId) return;
    try {
      const res = await fetch(`${API_URL}/api/gpt-pilot/projects/${jobId}/build/status`, { headers: authHeader() });
      if (res.ok) {
        const data = await res.json();
        setState(data);
      }
    } catch (e) { /* ignore */ }
  }, [jobId, authHeader]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const runBuild = async () => {
    setBusy(true);
    setState({ status: 'building', logs: '' });
    try {
      const res = await fetch(`${API_URL}/api/gpt-pilot/projects/${jobId}/build`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
      });
      if (res.status === 409) {
        toast.error('A build for this project is already running');
        await fetchStatus();
        return;
      }
      const rawBuild = await res.text();
      if (!res.ok) {
        throw new Error(rawBuild || `HTTP ${res.status}`);
      }
      let data;
      try { data = JSON.parse(rawBuild); } catch { data = {}; }
      setState(data);
      setIframeBust(Date.now());
      if (data.status === 'success') toast.success('Build complete — preview loaded');
      else toast.error(`Build ${data.status} at step ${data.step || '?'}`);
    } catch (e) {
      toast.error(`Build failed: ${e.message}`);
      setState({ status: 'failed', logs: e.message });
    } finally {
      setBusy(false);
    }
  };

  const publish = async () => {
    if (!state || state.status !== 'success') {
      toast.error('Run a successful build first');
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/gpt-pilot/projects/${jobId}/publish`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({}),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const fullUrl = `${API_URL}${data.url}`;
      setPublishUrl(fullUrl);
      try { await navigator.clipboard.writeText(fullUrl); toast.success('Public URL copied to clipboard'); }
      catch { toast.success('Published'); }
    } catch (e) {
      toast.error(`Publish failed: ${e.message}`);
    }
  };

  const previewUrl = state?.preview_url ? `${API_URL}${state.preview_url}` : null;
  const showIframe = state?.status === 'success' && previewUrl;
  const iframeSrc = showIframe ? `${previewUrl}?t=${iframeBust}` : null;

  return (
    <div className="h-[calc(100vh-160px)] min-h-[520px] flex flex-col" data-testid="live-build-panel">
      <div className="px-3 py-2 border-b border-white/[0.06] flex items-center justify-between gap-2 flex-shrink-0">
        <div className="flex items-center gap-2 text-[11px]">
          <span className="text-zinc-500">Build & serve static <span className="text-zinc-300 font-mono">dist/</span></span>
          {state?.status && (
            <span
              data-testid="build-status"
              className={`px-2 py-0.5 rounded text-[10px] font-medium ${
                state.status === 'building' ? 'bg-sky-500/15 text-sky-200 animate-pulse' :
                state.status === 'success' ? 'bg-emerald-500/15 text-emerald-200' :
                state.status === 'failed' ? 'bg-red-500/15 text-red-200' :
                'bg-zinc-500/15 text-zinc-300'
              }`}
            >
              {state.status}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          <button
            onClick={runBuild}
            disabled={busy || !jobId}
            data-testid="build-deploy-btn"
            className="px-3 py-1 rounded bg-violet-500/15 hover:bg-violet-500/25 disabled:opacity-40 text-violet-200 text-[11px] font-medium flex items-center gap-1.5"
            title="Run npm install + vite build inside the project's frontend root and serve the resulting dist/ at a public URL"
          >
            {busy
              ? <RefreshCw className="w-3 h-3 animate-spin" />
              : <Box className="w-3 h-3" />}
            {state?.status === 'success' ? 'Rebuild' : 'Build & Serve'}
          </button>
          {state?.status === 'success' && (
            <>
              <a
                href={iframeSrc || '#'}
                target="_blank"
                rel="noreferrer noopener"
                data-testid="open-preview-tab"
                className="px-2.5 py-1 rounded hover:bg-white/[0.06] text-zinc-300 text-[11px] flex items-center gap-1"
                title="Open the live preview in a new tab"
              >
                <ExternalLink className="w-3 h-3" />
                Open
              </a>
              <button
                onClick={publish}
                data-testid="publish-btn"
                className="px-2.5 py-1 rounded bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-200 text-[11px] font-medium flex items-center gap-1"
                title="Mark this build as published and copy the public URL"
              >
                <Globe className="w-3 h-3" />
                Publish
              </button>
            </>
          )}
        </div>
      </div>

      {publishUrl && (
        <div className="px-3 py-1.5 bg-emerald-500/10 border-b border-emerald-500/20 text-[11px] flex items-center justify-between gap-2 flex-shrink-0">
          <span className="text-emerald-200 truncate">
            Live at <a href={publishUrl} target="_blank" rel="noreferrer noopener" className="font-mono underline decoration-emerald-400/40 hover:decoration-emerald-300" data-testid="published-url">{publishUrl}</a>
          </span>
          <button
            onClick={() => { navigator.clipboard.writeText(publishUrl).then(() => toast.success('Copied')); }}
            className="text-emerald-200 hover:text-emerald-100 underline"
          >
            Copy
          </button>
        </div>
      )}

      <div className="flex-1 min-h-0 grid grid-cols-1 md:grid-cols-3">
        <div className="md:col-span-2 bg-white border-r border-white/[0.06] min-h-[300px]">
          {iframeSrc ? (
            <iframe
              key={iframeSrc}
              src={iframeSrc}
              title="Live build preview"
              className="w-full h-full border-0"
              sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
              data-testid="live-build-iframe"
            />
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-zinc-500 text-xs px-6 text-center bg-[#0a0a0a]">
              <Box className="w-7 h-7 mb-2 text-zinc-700" />
              {state?.status === 'building' && (
                <p className="text-zinc-300 animate-pulse">Building project… this takes 2–5 minutes on the first run</p>
              )}
              {(!state?.status || state.status === 'not_built') && (
                <>
                  <p className="text-zinc-300 mb-1">Not built yet</p>
                  <p>Click <span className="text-violet-300">Build &amp; Serve</span> to run <span className="font-mono">npm install + vite build</span> and get a shareable URL.</p>
                </>
              )}
              {state?.status === 'failed' && (
                <p className="text-red-300">Build failed at step <span className="font-mono">{state.step}</span>. Check the log on the right.</p>
              )}
            </div>
          )}
        </div>
        <div className="md:col-span-1 overflow-y-auto bg-[#080808] p-3" data-testid="build-logs">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5">Build logs</div>
          {state?.logs ? (
            <pre className="text-[10px] leading-relaxed font-mono whitespace-pre-wrap text-zinc-300">{state.logs}</pre>
          ) : (
            <p className="text-[10px] text-zinc-600 italic">No logs yet. Run a build to see install + bundler output here.</p>
          )}
        </div>
      </div>
    </div>
  );
}

// --- E2B instant preview — per-project cloud sandbox with public URL -----
function E2BPreviewPanel({ jobId, authHeader }) {
  const [status, setStatus] = useState('idle');   // idle | starting | installing | running | ready | verifying | dead | error | disabled
  const [url, setUrl] = useState(null);
  const [error, setError] = useState(null);
  const [template, setTemplate] = useState(null);
  const pollRef = useRef(null);
  const restartingRef = useRef(false);
  const verifyRef = useRef(null);

  // Client-side reachability check before showing the iframe.
  // The backend marks "ready" when IT can reach the URL (from the server),
  // but the user's browser may need a few more seconds (DNS propagation,
  // E2B edge routing). Without this check, the iframe immediately shows
  // "refused to connect" for a URL that works seconds later.
  const verifyReachable = useCallback((targetUrl) => {
    if (verifyRef.current) clearInterval(verifyRef.current);
    setStatus('verifying');
    let attempts = 0;
    const maxAttempts = 20; // 20 × 2s = 40s max
    const check = async () => {
      attempts++;
      try {
        // no-cors: we can't read the body, but a successful fetch means the
        // TCP connection was established (opaque response). A connection
        // refusal throws TypeError.
        await fetch(`${targetUrl}?_preflight=${Date.now()}`, { mode: 'no-cors', cache: 'no-store' });
        if (verifyRef.current) clearInterval(verifyRef.current);
        verifyRef.current = null;
        setStatus('ready');
      } catch {
        if (attempts >= maxAttempts) {
          if (verifyRef.current) clearInterval(verifyRef.current);
          verifyRef.current = null;
          setStatus('ready'); // show iframe anyway — let the user see the actual error
        }
        // else retry on next tick
      }
    };
    check(); // immediate first attempt
    verifyRef.current = setInterval(check, 2000);
  }, []);

  const applyStatus = useCallback((data) => {
    const newStatus = data.status || 'idle';
    if (data.url) setUrl(data.url);
    if (data.error) setError(data.error);
    if (data.template) setTemplate(data.template);
    // When backend transitions to 'ready', run a client-side reachability
    // check before actually showing the iframe.
    if (newStatus === 'ready' && data.url && status !== 'ready' && status !== 'verifying') {
      verifyReachable(data.url);
    } else if (newStatus !== 'ready') {
      setStatus(newStatus);
    }
  }, [status, verifyReachable]);

  const startPreview = useCallback(async () => {
    if (!jobId) return;
    restartingRef.current = false;
    setStatus('starting');
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/gpt-pilot/projects/${jobId}/preview/e2b/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
      });
      const data = await res.json();
      applyStatus(data);
    } catch (e) {
      setStatus('error');
      setError(e.message);
    }
  }, [jobId, authHeader, applyStatus]);

  // Tear the sandbox down and re-provision from scratch. Used by the manual
  // Restart button AND by auto-recovery when a live preview dies.
  const restart = useCallback(async () => {
    if (restartingRef.current) return;
    restartingRef.current = true;
    if (verifyRef.current) { clearInterval(verifyRef.current); verifyRef.current = null; }
    setUrl(null);
    setStatus('starting');
    try {
      await fetch(`${API_URL}/api/gpt-pilot/projects/${jobId}/preview/e2b/stop`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
      });
    } catch { /* best-effort teardown */ }
    await startPreview();
  }, [jobId, authHeader, startPreview]);

  const pollStatus = useCallback(async () => {
    if (!jobId || restartingRef.current) return;
    try {
      const res = await fetch(`${API_URL}/api/gpt-pilot/projects/${jobId}/preview/e2b/status`, {
        headers: authHeader(),
      });
      const data = await res.json();
      // The sandbox expired or crashed after going live (backend reports
      // 'dead'), or the backend pod recycled its in-memory registry while we
      // still hold a stale URL ('none'). Either way the iframe URL is now a
      // "refused to connect" \u2014 auto-recover by re-provisioning instead of
      // leaving a broken preview on screen.
      if (data.status === 'dead' || data.status === 'none') {
        restart();
        return;
      }
      applyStatus(data);
    } catch { /* retry next tick */ }
  }, [jobId, authHeader, applyStatus, restart]);

  // Clean up verify interval on unmount.
  useEffect(() => () => { if (verifyRef.current) clearInterval(verifyRef.current); }, []);

  // Poll continuously \u2014 even after 'ready'. Each poll keeps the sandbox alive
  // (the backend extends its E2B timeout on every touch) and detects a dead
  // sandbox so we can auto-restart. Stopping at 'ready' (the old behavior) let
  // the sandbox silently expire under the user, producing "refused to connect".
  useEffect(() => {
    if (!jobId) return;
    startPreview();
    pollRef.current = setInterval(pollStatus, 4000);
    return () => clearInterval(pollRef.current);
  }, [jobId, startPreview, pollStatus]);

  if (status === 'disabled') {
    return (
      <div className="flex flex-col items-center justify-center h-full text-zinc-500 text-xs px-6 text-center bg-[#0a0a0a]">
        <AlertTriangle className="w-6 h-6 mb-2 text-amber-500/50" />
        <p className="text-zinc-300 mb-1">Instant preview is not configured</p>
        <p>Set <code className="font-mono text-violet-300">E2B_API_KEY</code> on the backend to enable per-project cloud sandboxes.</p>
      </div>
    );
  }


  const isLoading = ['idle','starting','installing','running','verifying'].includes(status);
  const statusLabel = {
    idle: 'Initializing…',
    starting: 'Creating sandbox…',
    installing: 'Installing dependencies…',
    running: 'Starting dev server…',
    verifying: 'Connecting to preview…',
  }[status] || status;

  return (
    <div className="h-full flex flex-col" data-testid="e2b-preview-panel">
      {/* Toolbar */}
      <div className="px-3 py-1.5 border-b border-white/[0.06] flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-2 text-[11px]">
          {isLoading && <Loader2 className="w-3.5 h-3.5 animate-spin text-violet-300" />}
          {status === 'ready' && <span className="w-2 h-2 rounded-full bg-emerald-400 inline-block" />}
          {status === 'error' && <AlertTriangle className="w-3.5 h-3.5 text-red-400" />}
          <span className={`${isLoading ? 'text-violet-200 animate-pulse' : status === 'ready' ? 'text-emerald-200' : 'text-red-300'}`}>
            {statusLabel}
          </span>
          {template && <span className="text-zinc-600 text-[10px] font-mono">{template}</span>}
        </div>
        <div className="flex items-center gap-1.5">
          <button
            onClick={restart}
            disabled={isLoading}
            className="px-2.5 py-1 rounded hover:bg-white/[0.06] disabled:opacity-40 text-zinc-300 text-[11px] flex items-center gap-1"
            title="Restart preview sandbox"
          >
            <RefreshCw className="w-3 h-3" /> Restart
          </button>
          {status === 'ready' && url && (
            <a
              href={url}
              target="_blank"
              rel="noreferrer noopener"
              className="px-2.5 py-1 rounded hover:bg-white/[0.06] text-zinc-300 text-[11px] flex items-center gap-1"
              title="Open preview in new tab"
            >
              <ExternalLink className="w-3 h-3" /> Open
            </a>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 min-h-0 bg-white">
        {status === 'ready' && url ? (
          <iframe
            src={url}
            title="E2B instant preview"
            className="w-full h-full border-0"
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
            data-testid="e2b-preview-iframe"
          />
        ) : status === 'error' ? (
          <div className="flex flex-col items-center justify-center h-full text-zinc-500 text-xs px-6 text-center bg-[#0a0a0a]">
            <AlertTriangle className="w-7 h-7 mb-2 text-red-500/50" />
            <p className="text-red-300 mb-1">Preview failed</p>
            <p className="text-zinc-400 max-w-md font-mono text-[10px]">{error}</p>
            <button onClick={restart} className="mt-3 px-3 py-1 rounded bg-violet-500/15 text-violet-200 text-[11px] hover:bg-violet-500/25">
              Retry
            </button>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-full bg-[#0a0a0a]">
            <div className="relative mb-3">
              <Zap className="w-8 h-8 text-violet-400/40" />
              <Loader2 className="w-4 h-4 text-violet-300 animate-spin absolute -right-1 -bottom-1" />
            </div>
            <p className="text-zinc-300 text-xs animate-pulse">{statusLabel}</p>
            <p className="text-zinc-600 text-[10px] mt-1">Spinning up a dedicated cloud sandbox…</p>
          </div>
        )}
      </div>
    </div>
  );
}

// --- Fast live preview — Vite dev server (seconds) with static build fallback --
// Starts `npm run dev` on the backend and proxies via /api/gpt-pilot/preview-dev/.
// Falls back to dist/ at /api/gpt-pilot/preview/ if dev preview fails.
// Does not use /preview-port/* (circular proxy with CRA on :3001).
// --------------------------------------------------------------------------
function FastLivePreviewPanel({ jobId, authHeader, fallbackUrl, refreshToken, agentRunning }) {
  const [state, setState] = useState(null);
  const [previewMode, setPreviewMode] = useState('dev'); // 'dev' | 'static'
  const [busy, setBusy] = useState(false);
  const [iframeBust, setIframeBust] = useState(0);
  const autoStartedRef = useRef(false);
  const startInFlightRef = useRef(false);
  const staticFallbackRef = useRef(false);
  const pollRef = useRef(null);
  const prevStatusRef = useRef(null);

  const fetchDevStatus = useCallback(async () => {
    if (!jobId) return null;
    try {
      const res = await fetch(`${API_URL}/api/gpt-pilot/projects/${jobId}/preview/dev/status`, { headers: authHeader() });
      const { data } = await parseResponseJson(res);
      if (!res.ok) return null;
      setState(data);
      return data;
    } catch {
      return null;
    }
  }, [jobId, authHeader]);

  const fetchStaticStatus = useCallback(async () => {
    if (!jobId) return null;
    try {
      const res = await fetch(`${API_URL}/api/gpt-pilot/projects/${jobId}/build/status`, { headers: authHeader() });
      const { data } = await parseResponseJson(res);
      if (!res.ok) return null;
      return data;
    } catch {
      return null;
    }
  }, [jobId, authHeader]);

  const runStaticBuild = useCallback(async ({ silent = false } = {}) => {
    if (!jobId || startInFlightRef.current) return;
    startInFlightRef.current = true;
    setBusy(true);
    setPreviewMode('static');
    setState((s) => ({ ...(s || {}), status: 'building', logs: s?.logs || '' }));
    try {
      const res = await fetch(`${API_URL}/api/gpt-pilot/projects/${jobId}/build`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
      });
      if (res.status === 409) {
        const data = await fetchStaticStatus();
        if (data) setState(data);
        return;
      }
      const { data, text } = await parseResponseJson(res);
      if (!res.ok) throw new Error(formatApiDetail(data, text || `HTTP ${res.status}`));
      setState(data);
      if (data.status === 'success') {
        setIframeBust(Date.now());
        if (!silent) toast.success('Static preview ready');
      } else if (!silent) {
        toast.error(`Preview build ${data.status || 'failed'}`);
      }
    } catch (e) {
      setState({ status: 'failed', logs: e.message });
      if (!silent) toast.error(`Preview build failed: ${e.message}`);
    } finally {
      startInFlightRef.current = false;
      setBusy(false);
    }
  }, [jobId, authHeader, fetchStaticStatus]);

  const startDevPreview = useCallback(async ({ silent = false, force = false } = {}) => {
    if (!jobId || startInFlightRef.current) return;
    startInFlightRef.current = true;
    setBusy(true);
    setPreviewMode('dev');
    setState((s) => ({ ...(s || {}), status: 'starting', logs: s?.logs || '' }));
    try {
      const url = force
        ? `${API_URL}/api/gpt-pilot/projects/${jobId}/preview/dev/start?force=1`
        : `${API_URL}/api/gpt-pilot/projects/${jobId}/preview/dev/start`;
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
      });
      const { data, text } = await parseResponseJson(res);
      if (!res.ok) throw new Error(formatApiDetail(data, text || `HTTP ${res.status}`));
      setState(data);
      if (data.status === 'ready') {
        setIframeBust(Date.now());
        if (!silent) toast.success('Live preview ready');
        setBusy(false);
      } else if (data.status === 'failed') {
        if (!silent) toast.error('Dev preview failed — trying static build…');
        startInFlightRef.current = false;
        if (!staticFallbackRef.current) {
          staticFallbackRef.current = true;
          await runStaticBuild({ silent: true });
        }
        return;
      }
      // status === 'starting' — polling will flip to ready
    } catch (e) {
      setState({ status: 'failed', logs: e.message });
      if (!silent) toast.error(`Dev preview failed: ${e.message}`);
      startInFlightRef.current = false;
      if (!staticFallbackRef.current) {
        staticFallbackRef.current = true;
        await runStaticBuild({ silent: true });
      }
      return;
    } finally {
      if (startInFlightRef.current) startInFlightRef.current = false;
    }
  }, [jobId, authHeader, runStaticBuild]);

  const ensurePreview = useCallback(async () => {
    if (!jobId) return;
    const data = await fetchDevStatus();
    if (data?.status === 'ready') {
      setPreviewMode('dev');
      setIframeBust(Date.now());
      setBusy(false);
      return;
    }
    if (data?.status === 'starting') {
      setBusy(true);
      return;
    }
    if (!autoStartedRef.current) {
      autoStartedRef.current = true;
      startDevPreview({ silent: true });
    }
  }, [jobId, fetchDevStatus, startDevPreview]);

  useEffect(() => {
    if (!jobId) return undefined;
    autoStartedRef.current = false;
    staticFallbackRef.current = false;
    prevStatusRef.current = null;
    ensurePreview();
    pollRef.current = setInterval(async () => {
      const data = await fetchDevStatus();
      if (!data) return;
      const prev = prevStatusRef.current;
      if (prev !== 'ready' && data.status === 'ready') {
        setPreviewMode('dev');
        setIframeBust(Date.now());
        setBusy(false);
      } else if (data.status === 'starting') {
        setBusy(true);
      } else if (data.status === 'failed' && prev === 'starting') {
        setBusy(false);
        if (!staticFallbackRef.current) {
          staticFallbackRef.current = true;
          runStaticBuild({ silent: true });
        }
      }
      prevStatusRef.current = data.status;
    }, 2000);
    return () => clearInterval(pollRef.current);
  }, [jobId, ensurePreview, fetchDevStatus, runStaticBuild]);

  useEffect(() => {
    if (!jobId || !refreshToken) return;
    setIframeBust(Date.now());
  }, [refreshToken, jobId]);

  const devPreviewPath = gptPilotDevPreviewUrl(jobId, API_URL);
  const staticPreviewPath = gptPilotPreviewUrl(jobId, API_URL);
  const devUrl = previewMode === 'dev' && state?.status === 'ready'
    ? `${devPreviewPath}?t=${iframeBust}`
    : null;
  const staticUrl = previewMode === 'static' && state?.status === 'success'
    ? `${staticPreviewPath || `${API_URL}${state?.preview_url || ''}`}?t=${iframeBust}`
    : null;
  const safeFallback = (() => {
    if (!fallbackUrl) return null;
    const normalized = normalizePreviewUrl(fallbackUrl) || fallbackUrl;
    if (normalized.includes('/preview-port/') || isUnsafePreviewPortUrl(normalized)) return null;
    if (normalized.startsWith('/api/')) return `${API_URL}${normalized}`;
    if (normalized.startsWith('http')) return normalized;
    return `${window.location.origin}${normalized.startsWith('/') ? '' : '/'}${normalized}`;
  })();
  const iframeSrc = devUrl || staticUrl || safeFallback || null;
  const isBuilding = busy || state?.status === 'building' || state?.status === 'starting';
  const showIframe = Boolean(iframeSrc);

  return (
    <div className="h-full flex flex-col" data-testid="fast-live-preview-panel">
      <div className="px-3 py-1.5 border-b border-white/[0.06] flex items-center justify-between flex-shrink-0 bg-[#0a0a0a]">
        <div className="flex items-center gap-2 text-[11px]">
          {isBuilding && <Loader2 className="w-3.5 h-3.5 animate-spin text-violet-300" />}
          {!isBuilding && showIframe && <span className="w-2 h-2 rounded-full bg-emerald-400 inline-block" />}
          <span className={isBuilding ? 'text-violet-200' : 'text-emerald-200'}>
            {isBuilding ? 'Building preview…' : showIframe ? 'Live preview' : agentRunning ? 'Waiting for app files…' : 'Preview'}
          </span>
          {state?.status === 'failed' && (
            <span className="text-red-300 text-[10px]">build failed</span>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => startDevPreview({ silent: false, force: true })}
            disabled={!jobId || isBuilding}
            data-testid="preview-rebuild-btn"
            className="px-2.5 py-1 rounded hover:bg-white/[0.06] disabled:opacity-40 text-zinc-300 text-[11px] flex items-center gap-1"
            title="Restart Vite dev preview (falls back to static build if needed)"
          >
            <RefreshCw className={`w-3 h-3 ${isBuilding ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          {showIframe && iframeSrc && (
            <a
              href={iframeSrc}
              target="_blank"
              rel="noreferrer noopener"
              className="px-2.5 py-1 rounded hover:bg-white/[0.06] text-zinc-300 text-[11px] flex items-center gap-1"
            >
              <ExternalLink className="w-3 h-3" />
              Open
            </a>
          )}
        </div>
      </div>

      <div className="flex-1 min-h-0 bg-[#0a0a0a] p-3">
        <div className="h-full rounded-xl border border-white/[0.08] bg-white overflow-hidden shadow-2xl shadow-black/40">
          {showIframe ? (
            <iframe
              key={iframeSrc}
              src={iframeSrc}
              title="Live app preview"
              className="w-full h-full border-0"
              sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
              data-testid="fast-live-preview-iframe"
            />
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-center px-6 bg-gradient-to-b from-[#0f0f12] to-[#0a0a0a]">
              <div className="relative mb-4">
                <div className="w-16 h-16 rounded-2xl border border-violet-500/20 bg-violet-500/10 flex items-center justify-center">
                  <Sparkles className="w-7 h-7 text-violet-300" />
                </div>
                <Loader2 className="w-5 h-5 text-violet-300 animate-spin absolute -right-2 -bottom-2" />
              </div>
              <p className="text-zinc-200 text-sm font-medium">
                {agentRunning ? 'Your app is being built…' : isBuilding ? 'Compiling preview…' : 'Preview will appear here'}
              </p>
              <p className="text-zinc-500 text-xs mt-2 max-w-sm">
                {agentRunning
                  ? 'Watch the agent stream on the left. The preview auto-updates when files are ready.'
                  : isBuilding
                    ? 'Starting Vite dev server on the backend (first run installs deps — usually under 30s).'
                    : 'Start a build or open a project with generated files.'}
              </p>
              {state?.status === 'failed' && state?.logs && (
                <pre className="mt-4 max-h-32 overflow-auto text-left text-[10px] font-mono text-red-300/80 bg-black/40 rounded p-2 w-full max-w-lg">
                  {(state.logs || '').slice(-1200)}
                </pre>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// --- Preview pane — Live Preview (default, Emergent-style) | Build logs ---
function PreviewPane({ jobId, authHeader, fallbackUrl, refreshToken, agentRunning }) {
  const [mode, setMode] = useState('live'); // 'live' | 'build' | 'e2b'

  const tabs = [
    { id: 'live', label: 'Live Preview', icon: Sparkles, hint: 'Vite dev server — loads in seconds with hot reload' },
    { id: 'build', label: 'Build Logs', icon: Box, hint: 'Full build output, publish & deploy' },
  ];

  const active = tabs.find((t) => t.id === mode) || tabs[0];

  return (
    <div className="h-[calc(100vh-160px)] min-h-[520px] flex flex-col" data-testid="preview-pane">
      <div className="px-3 py-1.5 border-b border-white/[0.06] flex items-center gap-1 flex-shrink-0 bg-[#0a0a0a]">
        {tabs.map((t) => {
          const Icon = t.icon;
          const isActive = t.id === mode;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setMode(t.id)}
              data-testid={`preview-tab-${t.id}`}
              title={t.hint}
              className={`px-2.5 py-1 rounded text-[11px] font-medium flex items-center gap-1 transition-colors ${
                isActive ? 'bg-violet-500/20 text-violet-100' : 'text-zinc-500 hover:text-zinc-300 hover:bg-white/[0.04]'
              }`}
            >
              <Icon className="w-3 h-3" />
              {t.label}
            </button>
          );
        })}
        <span className="ml-auto text-[10px] text-zinc-600 hidden md:inline">{active.hint}</span>
      </div>

      <div className="flex-1 min-h-0 relative">
        <div className="absolute inset-0" style={{ display: mode === 'live' ? 'block' : 'none' }}>
          <FastLivePreviewPanel
            jobId={jobId}
            authHeader={authHeader}
            fallbackUrl={fallbackUrl}
            refreshToken={refreshToken}
            agentRunning={agentRunning}
          />
        </div>
        {mode === 'build' && (
          <div className="absolute inset-0">
            <LiveBuildPanel jobId={jobId} authHeader={authHeader} />
          </div>
        )}
      </div>
    </div>
  );
}

// --- Chat panel — Devonz-style iterative editing on the existing project ---
// User types e.g. "make the hero pink, add a contact form" → backend asks
// the LLM for minimum file edits → writes them to disk → auto-rebuilds dist/
// → same /preview/<job_id>/ URL serves the new content.
// --------------------------------------------------------------------------
function ChatPanel({ jobId, authHeader, onStartProject, running, status, onStop }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [autoBuild, setAutoBuild] = useState(true);
  const [autonomous, setAutonomous] = useState(false);
  const [agentEvents, setAgentEvents] = useState([]);
  const [attachments, setAttachments] = useState([]); // [{name,type,data:base64,preview?:dataURL}]
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);
  const scrollRef = useRef(null);

  const MAX_ATTACHMENTS = 5;
  const MAX_FILE_BYTES = 5 * 1024 * 1024; // 5 MB

  // Pre-fill the composer if Dashboard "Resume" set a sessionStorage value.
  useEffect(() => {
    try {
      const pre = sessionStorage.getItem('snagcart_chat_prefill');
      if (pre) {
        setInput(pre);
        sessionStorage.removeItem('snagcart_chat_prefill');
      }
    } catch { /* ignore */ }
  }, []);

  const readFileToAttachment = useCallback((file) => new Promise((resolve, reject) => {
    if (file.size > MAX_FILE_BYTES) {
      reject(new Error(`${file.name} is larger than 5 MB`));
      return;
    }
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error || new Error('Read failed'));
    reader.onload = () => {
      const dataUrl = reader.result || '';
      const m = /^data:([^;]+);base64,(.*)$/.exec(dataUrl);
      if (!m) { reject(new Error('Could not encode')); return; }
      resolve({
        name: file.name,
        type: m[1],
        data: m[2],
        preview: m[1].startsWith('image/') ? dataUrl : null,
      });
    };
    reader.readAsDataURL(file);
  }), []);

  const addFiles = useCallback(async (files) => {
    const accepted = [];
    for (const f of files) {
      if (attachments.length + accepted.length >= MAX_ATTACHMENTS) {
        toast.error(`Up to ${MAX_ATTACHMENTS} attachments per message`);
        break;
      }
      try {
        accepted.push(await readFileToAttachment(f));
      } catch (e) {
        toast.error(e.message || `Could not attach ${f.name}`);
      }
    }
    if (accepted.length) setAttachments((prev) => [...prev, ...accepted]);
  }, [attachments.length, readFileToAttachment]);

  const onFilePick = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length) addFiles(files);
    e.target.value = ''; // allow re-selecting the same file
  };

  const onPaste = (e) => {
    const items = Array.from(e.clipboardData?.items || []);
    const files = items.map((i) => (i.kind === 'file' ? i.getAsFile() : null)).filter(Boolean);
    if (files.length) {
      e.preventDefault();
      addFiles(files);
    }
  };

  const onDragOver = (e) => { e.preventDefault(); setDragActive(true); };
  const onDragLeave = () => setDragActive(false);
  const onDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    const files = Array.from(e.dataTransfer?.files || []);
    if (files.length) addFiles(files);
  };

  // Load persisted history when a job is selected.
  useEffect(() => {
    if (!jobId) return;
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/gpt-pilot/projects/${jobId}/chat`, { headers: authHeader() });
        if (!res.ok) return;
        const data = await res.json();
        if (alive) setMessages(data.chat || []);
      } catch { /* ignore */ }
    })();
    return () => { alive = false; };
  }, [jobId, authHeader]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending, agentEvents]);

  const buildBodyAttachments = () => attachments.map(({ name, type, data }) => ({ name, type, data }));

  const runAutonomousAgent = async (text, atts) => {
    // Step 1: queue the goal + attachments.
    const queue = await fetch(`${API_URL}/api/gpt-pilot/projects/${jobId}/agent`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeader() },
      body: JSON.stringify({ message: text, attachments: atts }),
    });
    if (!queue.ok) {
      const err = await queue.text();
      throw new Error(err || `HTTP ${queue.status}`);
    }
    // Step 2: open SSE stream.
    const token = localStorage.getItem('token') || '';
    const streamUrl = `${API_URL}/api/gpt-pilot/projects/${jobId}/agent/stream?token=${encodeURIComponent(token)}`;
    const res = await fetch(streamUrl, { headers: { Accept: 'text/event-stream' } });
    if (!res.ok || !res.body) throw new Error(`Stream HTTP ${res.status}`);
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buf = '';
    let lastFinal = null;
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });
      let idx;
      while ((idx = buf.indexOf('\n\n')) !== -1) {
        const frame = buf.slice(0, idx).trim();
        buf = buf.slice(idx + 2);
        if (!frame.startsWith('data:')) continue;
        try {
          const ev = JSON.parse(frame.slice(5).trim());
          setAgentEvents((prev) => [...prev, ev]);
          if (ev.type === 'final') lastFinal = ev;
          if (ev.type === 'end') return lastFinal;
        } catch { /* ignore */ }
      }
    }
    return lastFinal;
  };

  const send = async () => {
    const rawText = input.trim();
    if ((!rawText && attachments.length === 0) || sending) return;

    // No job yet — first prompt creates a new build (Emergent-style unified chat)
    if (!jobId) {
      if (!rawText) {
        toast.error('Type what you want to build before attaching files');
        return;
      }
      if (typeof onStartProject !== 'function') return;
      const ts = new Date().toISOString();
      setMessages((m) => [...m, { role: 'user', content: rawText, ts }]);
      setInput('');
      setAttachments([]);
      const newJobId = await onStartProject(rawText);
      if (newJobId) {
        setMessages((m) => [...m, {
          role: 'assistant',
          content: 'Build started. Watch the live agent stream on the right — Spec → Architect → Developer → Code Monkey → Reviewer.',
          ts: new Date().toISOString(),
        }]);
      }
      return;
    }

    // Pull live settings (Project Memory, notifications, etc.)
    const s = readSnagcartSettings();
    const mem = (s.project_memory && s.project_memory.enabled && (s.project_memory.memory_text || '').trim()) || '';
    const text = mem ? `${mem}\n\n---\n\n${rawText || 'Match this reference.'}` : rawText;
    setSending(true);
    setAgentEvents([]);
    const ts = new Date().toISOString();
    const sentAttachments = attachments.map((a) => ({ name: a.name, type: a.type, preview: a.preview }));
    const bodyAtts = buildBodyAttachments();
    setMessages((m) => [...m, { role: 'user', content: rawText || '(see attached)', attachments: sentAttachments, ts }]);
    setInput('');
    setAttachments([]);
    try {
      if (autonomous) {
        const final = await runAutonomousAgent(text || 'Match this reference.', bodyAtts);
        try {
          const res = await fetch(`${API_URL}/api/gpt-pilot/projects/${jobId}/chat`, { headers: authHeader() });
          if (res.ok) {
            const data = await res.json();
            setMessages(data.chat || []);
          }
        } catch { /* ignore */ }
        if (s?.notifications?.toast_on_agent_done !== false) {
          toast.success(final?.summary || 'Agent finished');
        }
      } else {
        const res = await fetch(`${API_URL}/api/gpt-pilot/projects/${jobId}/chat`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeader() },
          body: JSON.stringify({
            message: text || 'Match this reference.',
            auto_build: autoBuild,
            attachments: bodyAtts,
            extended_thinking: !!s?.core?.extended_thinking,
            optimize_context: s?.core?.context_optimization !== false,
          }),
        });
        const rawBody = await res.text();
        if (!res.ok) {
          throw new Error(rawBody || `HTTP ${res.status}`);
        }
        let data;
        try { data = JSON.parse(rawBody); } catch { data = {}; }
        setMessages((m) => [...m, {
          role: 'assistant',
          content: data.summary,
          files_changed: data.files_changed,
          files_skipped: data.files_skipped,
          build: data.build,
          verification: data.verification,
          swept_files: data.swept_files,
          intent_expanded: data.intent_expanded,
          edit_actions: data.edit_actions,
          rules_loaded: data.rules_loaded,
          ts: new Date().toISOString(),
        }]);
        // Surface the verification result as the dominant signal.
        const v = data.verification;
        if (Array.isArray(data.swept_files) && data.swept_files.length > 0) {
          toast.message(`Image sweep: rewrote URLs in ${data.swept_files.length} file(s) before the LLM ran`);
        }
        if (v && v.verified === true) {
          toast.success(`✅ Fix verified · ${v.summary || ''}`);
        } else if (v && v.verified === false) {
          toast.error(`⚠️ Fix attempted but not verified — ${v.summary || 'see remaining issues'}`);
        } else if (v && v.verified === null) {
          toast.message(`Verification skipped: ${v.summary || 'could not screenshot preview'}`);
        } else if (data.build && data.build.status === 'success') {
          if (s?.notifications?.toast_on_chat_reply !== false) toast.success(`Updated ${data.files_changed?.length || 0} file(s) — preview rebuilt`);
        } else if (data.build && data.build.status === 'failed') {
          toast.error('Edits applied but build failed');
        } else if (data.files_changed?.length) {
          if (s?.notifications?.toast_on_chat_reply !== false) toast.success(`Updated ${data.files_changed.length} file(s)`);
        }
      }
    } catch (e) {
      setMessages((m) => [...m, { role: 'assistant', content: `Error: ${e.message}`, error: true, ts: new Date().toISOString() }]);
      toast.error(`Chat failed: ${e.message}`);
    } finally {
      setSending(false);
      setAgentEvents([]);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      send();
    }
  };

  return (
    <div
      className={`h-[calc(100vh-200px)] min-h-[480px] flex flex-col relative ${dragActive ? 'ring-2 ring-violet-500/60 ring-inset' : ''}`}
      data-testid="chat-pane"
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      {dragActive && (
        <div className="absolute inset-0 z-20 bg-violet-500/10 border-2 border-dashed border-violet-500/50 flex items-center justify-center pointer-events-none">
          <p className="text-violet-200 text-sm font-medium">Drop image(s) to attach</p>
        </div>
      )}
      <div className="px-4 py-2 border-b border-white/[0.06] flex items-center gap-3 flex-shrink-0 bg-[#0a0a0a]">
        <span className="text-[11px] text-zinc-400">
          <MessageSquare className="w-3 h-3 inline mr-1 text-violet-300" />
          {!jobId
            ? 'Send your first prompt to kick off a build'
            : (autonomous ? 'Autonomous agent — sees the live preview, iterates until done' : 'One-shot edit — single LLM call')}
        </span>
        <div className="flex-1" />
        {running && typeof onStop === 'function' && (
          <button
            type="button"
            onClick={onStop}
            data-testid="chat-stop-build"
            className="text-[10px] text-red-300 hover:text-red-200 inline-flex items-center gap-1 px-2 py-0.5 rounded border border-red-500/30 hover:bg-red-500/10"
            title="Stop the build stream"
          >
            ■ Stop
          </button>
        )}
        {!running && status && (
          <span className="text-[10px] text-zinc-500 uppercase tracking-wider">{status}</span>
        )}
        {jobId && (
          <>
            <label className="text-[10px] text-zinc-500 flex items-center gap-1 cursor-pointer select-none" title="Vision-driven loop: agent screenshots the preview, edits, rebuilds, re-screenshots, repeats">
              <input
                type="checkbox"
                checked={autonomous}
                onChange={(e) => setAutonomous(e.target.checked)}
                data-testid="chat-autonomous"
                className="accent-violet-500"
              />
              autonomous
            </label>
            {!autonomous && (
              <label className="text-[10px] text-zinc-500 flex items-center gap-1 cursor-pointer select-none" title="Auto-run vite build after the edit">
                <input
                  type="checkbox"
                  checked={autoBuild}
                  onChange={(e) => setAutoBuild(e.target.checked)}
                  data-testid="chat-auto-build"
                  className="accent-violet-500"
                />
                auto-build
              </label>
            )}
          </>
        )}
      </div>

      <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto px-4 py-3 space-y-3" data-testid="chat-messages">
        {messages.length === 0 && !sending && !running && !jobId && (
          <div className="text-zinc-400 text-xs mt-2 space-y-3" data-testid="chat-empty-state">
            <p className="text-zinc-300">
              <Sparkles className="w-3.5 h-3.5 inline mr-1.5 text-violet-300" />
              Describe the website or app you want to build. Hit <span className="text-violet-200">Send</span> to kick off your first build.
            </p>
            <div className="border border-white/[0.06] rounded-lg p-3 bg-white/[0.02]">
              <p className="text-[11px] text-zinc-500 mb-1.5">Try one of these to get started:</p>
              <div className="flex flex-col gap-1.5">
                {[
                  'A FastAPI todo app with auth, CRUD endpoints for tasks, and a React frontend with TailwindCSS.',
                  'Landing page for a coffee shop with hero, menu grid, contact form, dark theme.',
                  'Portfolio site for a freelance designer — bold typography, project gallery, contact CTA.',
                ].map((p) => (
                  <button
                    key={p}
                    onClick={() => setInput(p)}
                    data-testid="chat-suggestion"
                    className="text-left text-[11px] text-zinc-400 hover:text-violet-200 truncate"
                  >
                    →&nbsp;{p}
                  </button>
                ))}
              </div>
            </div>
            <p className="text-[11px] text-zinc-500">
              Once your project is running, the same chat continues — ask Snagcart to add features, change colours,
              fix bugs, or rebuild a page from a screenshot.
            </p>
          </div>
        )}
        {messages.length === 0 && !sending && jobId && (
          <div className="text-zinc-500 text-xs italic mt-2">
            Type an instruction below. With <span className="text-violet-300">autonomous</span> mode on, the agent will:
            <ol className="mt-2 space-y-1 list-decimal list-inside text-zinc-600">
              <li>Open your live preview in a headless browser and take a screenshot.</li>
              <li>Reason about what needs to change.</li>
              <li>Edit the right files and rebuild.</li>
              <li>Take a fresh screenshot — repeat until the goal looks done.</li>
            </ol>
            <p className="mt-2 text-zinc-500">
              <Paperclip className="w-3 h-3 inline mr-1" />
              Tip: attach a screenshot / mockup / Figma snapshot and the agent will use it as the visual target.
            </p>
          </div>
        )}
        {messages.map((m, i) => (
          <ChatBubble key={i} msg={m} />
        ))}
        {running && !jobId && (
          <div className="flex items-center gap-2 text-zinc-400 text-xs italic" data-testid="chat-build-starting">
            <Loader2 className="w-3.5 h-3.5 animate-spin text-violet-300" />
            Spinning up your build — Spec → Architect → Developer → Code Monkey → Reviewer.
          </div>
        )}
        {sending && autonomous && (
          <AgentLiveTrace events={agentEvents} />
        )}
        {sending && !autonomous && (
          <div className="flex items-center gap-2 text-zinc-500 text-xs italic" data-testid="chat-thinking">
            <Loader2 className="w-3.5 h-3.5 animate-spin text-violet-300" />
            Snagcart is editing your project…
          </div>
        )}
      </div>

      <div className="border-t border-white/[0.06] p-3 flex-shrink-0 bg-[#0a0a0a]">
        {attachments.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-2" data-testid="chat-attachment-list">
            {attachments.map((a, i) => (
              <AttachmentChip key={i} att={a} onRemove={() => setAttachments((p) => p.filter((_, j) => j !== i))} />
            ))}
          </div>
        )}
        <div className="flex gap-2 items-end">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*,.pdf,.txt,.md,.json,.csv"
            className="hidden"
            onChange={onFilePick}
            data-testid="chat-file-input"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={sending || running || !jobId || attachments.length >= MAX_ATTACHMENTS}
            data-testid="chat-attach-btn"
            title={jobId ? "Attach screenshots / mockups / reference images (up to 5, 5 MB each)" : "Attachments unlock once your first build is running"}
            className="h-[60px] w-[44px] rounded-lg bg-white/[0.04] hover:bg-white/[0.08] disabled:opacity-40 text-zinc-300 flex items-center justify-center transition-colors"
          >
            <Paperclip className="w-4 h-4" />
          </button>
          <VoiceInput
            disabled={sending || running}
            compact
            onTranscript={(text) => setInput(text)}
          />
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKeyDown}
            onPaste={onPaste}
            disabled={sending || running}
            rows={2}
            placeholder={jobId ? "Describe the goal — or paste / drop a reference image and say 'match this'" : 'Describe the website or app you want to build…'}
            data-testid="chat-input"
            className="flex-1 bg-[#080808] border border-white/[0.08] rounded-lg px-3 py-2 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-violet-500/50 resize-none"
          />
          <Button
            onClick={send}
            disabled={(!input.trim() && attachments.length === 0) || sending || running}
            data-testid="chat-send"
            className="bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 text-white px-4 h-[60px]"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </Button>
        </div>
        <p className="text-[10px] text-zinc-600 mt-1.5">
          ⌘/Ctrl + Enter to send · paste / drop / attach images, mockups, or text files for reference.
        </p>
      </div>
    </div>
  );
}

function AttachmentChip({ att, onRemove }) {
  const isImg = (att.type || '').startsWith('image/');
  return (
    <div className="flex items-center gap-1.5 bg-white/[0.06] border border-white/[0.1] rounded-lg pl-1.5 pr-1 py-1 text-[11px] text-zinc-200 max-w-[180px]" data-testid="chat-attachment-chip">
      {isImg && att.preview ? (
        <img src={att.preview} alt={att.name} className="w-6 h-6 rounded object-cover flex-shrink-0" />
      ) : (
        <ImageIcon className="w-3.5 h-3.5 text-zinc-400 flex-shrink-0" />
      )}
      <span className="truncate text-[11px]" title={att.name}>{att.name}</span>
      <button onClick={onRemove} className="text-zinc-500 hover:text-red-300 ml-0.5" data-testid="chat-attachment-remove">
        <XIcon className="w-3 h-3" />
      </button>
    </div>
  );
}

function AgentLiveTrace({ events }) {
  // Render a compact stream of what the agent is doing right now.
  return (
    <div className="rounded-2xl bg-white/[0.04] border border-white/[0.06] p-3 space-y-1.5" data-testid="agent-trace">
      <p className="text-[10px] uppercase tracking-wider text-violet-300 mb-1 flex items-center gap-1.5">
        <Loader2 className="w-3 h-3 animate-spin" />
        Autonomous agent running
      </p>
      {events.map((ev, i) => (
        <AgentTraceLine key={i} ev={ev} />
      ))}
    </div>
  );
}

function AgentTraceLine({ ev }) {
  const t = ev.type;
  if (t === 'iteration') {
    return <div className="text-[11px] text-violet-200 font-semibold mt-2">— Iteration {ev.n} / {ev.max} —</div>;
  }
  if (t === 'status') {
    return <div className="text-[11px] text-sky-300">› {ev.message}</div>;
  }
  if (t === 'screenshot') {
    return <div className="text-[11px] text-zinc-400">📸 {ev.captured ? `Screenshot captured (${Math.round((ev.bytes || 0) / 1024)} kB)` : `Screenshot skipped: ${ev.warning || ''}`}</div>;
  }
  if (t === 'context') {
    return <div className="text-[11px] text-zinc-500">📂 Sent {ev.files} files as context</div>;
  }
  if (t === 'thought') {
    return <div className="text-[11px] text-amber-200 italic">💭 {ev.content}</div>;
  }
  if (t === 'edits') {
    return (
      <div className="text-[11px]">
        <div className="text-emerald-300">✓ {ev.summary}</div>
        {ev.written?.length > 0 && (
          <div className="ml-4 mt-0.5 text-zinc-400 font-mono text-[10px]">
            {ev.written.map((p) => <div key={p}>• {p}</div>)}
          </div>
        )}
      </div>
    );
  }
  if (t === 'build') {
    return <div className={`text-[11px] ${ev.status === 'success' ? 'text-emerald-300' : 'text-red-300'}`}>🔨 Build {ev.status}</div>;
  }
  if (t === 'build_progress') {
    return <div className="text-[11px] text-zinc-500">⏱ Build running… {ev.elapsed_s}s</div>;
  }
  if (t === 'done') {
    return <div className="text-[11px] text-amber-200 mt-1">🎯 Implementer says done — running verifier…</div>;
  }
  if (t === 'verifier') {
    const pass = ev.verdict === 'PASS';
    return (
      <div className={`text-[11px] mt-1 ${pass ? 'text-emerald-300' : 'text-amber-300'}`}>
        {pass ? '✅' : '🔁'} Verifier: <span className="font-semibold">{ev.verdict}</span>
        {ev.summary ? ` — ${ev.summary}` : ''}
        {!pass && ev.issues?.length > 0 && (
          <ul className="ml-4 mt-0.5 list-disc text-amber-200/80">
            {ev.issues.slice(0, 6).map((it, idx) => <li key={idx}>{String(it)}</li>)}
          </ul>
        )}
      </div>
    );
  }
  if (t === 'final') {
    const passed = ev.verdict === 'PASS' || ev.completed;
    return (
      <div className={`text-[12px] mt-2 font-semibold ${passed ? 'text-emerald-300' : 'text-amber-300'}`}>
        {passed
          ? `✅ Build complete — verifier passed in ${ev.iterations} iteration(s).`
          : `⚠ Stopped after ${ev.iterations} iteration(s) — ${ev.stop_reason || 'incomplete'}. ${ev.summary || ''}`}
      </div>
    );
  }
  if (t === 'error' || t === 'warning') {
    return <div className={`text-[11px] ${t === 'error' ? 'text-red-300' : 'text-amber-300'}`}>⚠ {ev.message}</div>;
  }
  return null;
}

function ChatBubble({ msg }) {
  const isUser = msg.role === 'user';
  const isError = msg.error;
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-[13px] leading-relaxed ${
          isUser
            ? 'bg-violet-600 text-white'
            : isError
              ? 'bg-red-500/15 border border-red-500/30 text-red-200'
              : 'bg-white/[0.06] border border-white/[0.08] text-zinc-100'
        }`}
        data-testid={isUser ? 'chat-user-msg' : 'chat-assistant-msg'}
      >
        <p className="whitespace-pre-wrap break-words">{msg.content}</p>
        {isUser && Array.isArray(msg.attachments) && msg.attachments.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5" data-testid="chat-msg-attachments">
            {msg.attachments.map((a, j) => (
              a.preview ? (
                <img key={j} src={a.preview} alt={a.name} className="w-16 h-16 rounded object-cover border border-white/20" />
              ) : (
                <span key={j} className="text-[10px] bg-white/20 rounded px-1.5 py-0.5">{a.name}</span>
              )
            ))}
          </div>
        )}
        {!isUser && Array.isArray(msg.files_changed) && msg.files_changed.length > 0 && (
          <div className="mt-2 pt-2 border-t border-white/[0.08]">
            <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 flex items-center gap-1.5">
              Files updated
              {msg.rules_loaded && (
                <span className="px-1.5 py-0.5 rounded bg-cyan-500/15 text-cyan-300 text-[9px] uppercase tracking-wider">rules.md loaded</span>
              )}
            </p>
            <ul className="space-y-0.5">
              {msg.files_changed.map((p) => {
                const actionEntry = Array.isArray(msg.edit_actions) ? msg.edit_actions.find((a) => a.path === p) : null;
                const action = actionEntry?.action;
                return (
                  <li key={p} className="text-[11px] font-mono text-emerald-300 flex items-center gap-1.5">
                    <FileText className="w-3 h-3 opacity-60" />
                    <span>{p}</span>
                    {action === 'replace' && (
                      <span className="ml-auto px-1 py-0.5 rounded bg-emerald-500/15 text-emerald-200 text-[9px] uppercase tracking-wider" title="Surgical search-and-replace (cheaper, more accurate)">replace</span>
                    )}
                    {action === 'write' && (
                      <span className="ml-auto px-1 py-0.5 rounded bg-indigo-500/15 text-indigo-200 text-[9px] uppercase tracking-wider" title="Full file rewrite">write</span>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        )}
        {!isUser && Array.isArray(msg.swept_files) && msg.swept_files.length > 0 && (
          <div
            data-testid="chat-image-sweep"
            className="mt-2 pt-2 border-t border-white/[0.08]"
          >
            <p className="text-[10px] uppercase tracking-wider text-cyan-400/80 mb-1 flex items-center gap-1.5">
              <span>🧹</span> Image sweep · pre-LLM pass
              {msg.intent_expanded && (
                <span className="ml-1 px-1.5 py-0.5 rounded bg-cyan-500/15 text-cyan-300 text-[9px] uppercase tracking-wider">
                  prompt auto-expanded
                </span>
              )}
            </p>
            <p className="text-[11px] text-zinc-400 mb-1">
              Rewrote hallucinated/non-whitelisted image URLs in {msg.swept_files.length} file(s):
            </p>
            <ul className="space-y-0.5">
              {msg.swept_files.slice(0, 8).map((p) => (
                <li key={p} className="text-[11px] font-mono text-cyan-300/90 flex items-center gap-1.5">
                  <FileText className="w-3 h-3 opacity-60" />{p}
                </li>
              ))}
              {msg.swept_files.length > 8 && (
                <li className="text-[10px] text-zinc-500 italic">…and {msg.swept_files.length - 8} more</li>
              )}
            </ul>
          </div>
        )}
        {!isUser && Array.isArray(msg.files_skipped) && msg.files_skipped.length > 0 && (
          <div className="mt-2 pt-2 border-t border-white/[0.08]">
            <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Skipped</p>
            <ul className="space-y-0.5">
              {msg.files_skipped.slice(0, 5).map((f, i) => (
                <li key={i} className="text-[10px] font-mono text-amber-300">{f.path} — {f.reason}</li>
              ))}
            </ul>
          </div>
        )}
        {!isUser && msg.verification && (
          <div
            data-testid="chat-verification"
            className={`mt-2 pt-2 border-t border-white/[0.08] text-[11px] ${
              msg.verification.verified === true
                ? 'text-emerald-300'
                : msg.verification.verified === false
                  ? 'text-amber-300'
                  : 'text-zinc-400'
            }`}
          >
            <div className="flex items-center gap-1.5">
              <span className="text-base leading-none">
                {msg.verification.verified === true ? '✅' : msg.verification.verified === false ? '⚠️' : '🔍'}
              </span>
              <span className="font-medium">
                {msg.verification.verified === true
                  ? 'Fix verified visually'
                  : msg.verification.verified === false
                    ? 'Fix attempted — not yet verified'
                    : 'Verification skipped'}
              </span>
              {msg.verification.confidence && (
                <span className="text-[10px] uppercase tracking-wider text-zinc-500 ml-1">
                  · {msg.verification.confidence}
                </span>
              )}
            </div>
            {msg.verification.summary && (
              <p className="mt-1 text-[11px] text-zinc-300 italic">{msg.verification.summary}</p>
            )}
            {Array.isArray(msg.verification.remaining_issues) && msg.verification.remaining_issues.length > 0 && (
              <ul className="mt-1.5 space-y-0.5">
                {msg.verification.remaining_issues.map((issue, i) => (
                  <li key={i} className="text-[10px] text-amber-300 flex items-start gap-1">
                    <span>•</span> <span>{issue}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
        {!isUser && msg.build && (
          <div className="mt-2 pt-2 border-t border-white/[0.08] text-[10px]">
            <span className={`px-1.5 py-0.5 rounded font-medium ${
              msg.build.status === 'success' ? 'bg-emerald-500/20 text-emerald-200' :
              msg.build.status === 'failed' ? 'bg-red-500/20 text-red-200' :
              'bg-zinc-500/20 text-zinc-300'
            }`}>
              build: {msg.build.status}
            </span>
            {msg.build.status === 'success' && (
              <span className="text-zinc-500 ml-2">Preview refreshed</span>
            )}
            {msg.build.status === 'failed' && msg.build.logs && (
              <pre className="mt-1.5 text-[10px] font-mono whitespace-pre-wrap text-red-300/80 max-h-32 overflow-y-auto">
                {msg.build.logs.slice(-1200)}
              </pre>
            )}
          </div>
        )}
      </div>
    </div>
  );
}


